import React, { useEffect, useState } from "react";
import { MdOutlineConnectedTv } from "react-icons/md";
import ConnectTvService from "services/liveTv/ConnectTvService";
import LoadingPinSkeleton from "./LoadingPinSkeleton";
import "styles/connectTvLoading.css";

function ConnectTvPopUp() {
  const [pinValueArray, setPinValueArray] = useState<number[]>([]);
  const [pinData, setPinData] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [totalTime, setTotalTime] = useState<number>(0);

  const fetchPin = () => {
    setIsLoading(true);
    ConnectTvService.generatePin().then((res) => {
      setPinData(res);
      setIsLoading(false);
    });
  };

  useEffect(() => {
    fetchPin();
  }, []);

  useEffect(() => {
    if (!pinData) return;

    const expiresAt = new Date(pinData.expires_at).getTime();
    const now = new Date().getTime();
    const initialTimeLeft = Math.floor((expiresAt - now) / 1000);
    setTimeLeft(initialTimeLeft);
    setTotalTime(initialTimeLeft);

    const intervalId = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(intervalId);
          fetchPin();
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [pinData]);

  const convertNumberToArray = (num: number): number[] => {
    return num
      ?.toString()
      .split("")
      .map((digit) => parseInt(digit, 10));
  };

  useEffect(() => {
    setPinValueArray(convertNumberToArray(pinData?.pin));
  }, [pinData]);

  const mmss = `${Math.floor(timeLeft / 60)}:${String(timeLeft % 60).padStart(
    2,
    "0"
  )}`;
  const progress = totalTime > 0 ? Math.max(0, timeLeft / totalTime) : 0;

  // circular countdown ring math
  const R = 32;
  const CIRC = 2 * Math.PI * R;
  const dashOffset = CIRC * (1 - progress);

  return (
    <div className="relative mx-auto w-full max-w-md overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-b from-[#16161b] to-[#0d0d10] p-8 text-center shadow-[0_24px_70px_rgba(0,0,0,0.55)] max-sm:p-6">
      {/* ambient glow */}
      <div className="pointer-events-none absolute -top-20 left-1/2 h-44 w-44 -translate-x-1/2 rounded-full bg-[var(--red,#F50537)]/20 blur-3xl" />

      <div className="relative">
        {/* icon with circular countdown ring */}
        <div className="relative mx-auto mb-5 h-20 w-20">
          <svg className="absolute inset-0 -rotate-90" viewBox="0 0 72 72">
            <circle
              cx="36"
              cy="36"
              r={R}
              fill="none"
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="3"
            />
            <circle
              cx="36"
              cy="36"
              r={R}
              fill="none"
              stroke="var(--red,#F50537)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray={CIRC}
              strokeDashoffset={dashOffset}
              style={{ transition: "stroke-dashoffset 1s linear" }}
            />
          </svg>
          <div className="absolute inset-[10px] flex items-center justify-center rounded-full bg-[var(--red,#F50537)]/15">
            <MdOutlineConnectedTv className="text-3xl text-[var(--red,#F50537)]" />
          </div>
        </div>

        {/* heading */}
        <h2 className="text-xl font-bold text-white">Connect to your TV</h2>
        <p className="mx-auto mt-1 max-w-xs text-[13px] leading-relaxed text-white/50">
          Open the TV app, choose{" "}
          <strong className="text-white/80">Login via Pin</strong>, enter the
          code, then press Submit.
        </p>

        {isLoading ? (
          <div className="mt-7">
            <LoadingPinSkeleton />
          </div>
        ) : (
          <>
            {/* pin cells */}
            <div className="mt-7 flex items-center justify-center gap-2.5 max-sm:gap-2">
              {pinValueArray &&
                pinValueArray.map((pinValue, index) => (
                  <div
                    key={index}
                    className="flex h-14 w-12 items-center justify-center rounded-xl border border-[var(--red,#F50537)]/40 bg-gradient-to-b from-[var(--red,#F50537)]/20 to-[var(--red,#F50537)]/5 text-2xl font-bold tabular-nums text-white shadow-[0_4px_16px_rgba(245,5,55,0.18)] max-sm:h-12 max-sm:w-10"
                  >
                    {pinValue}
                  </div>
                ))}
            </div>

            {/* countdown */}
            <div className="mt-6 flex items-center justify-center gap-2 text-sm">
              <span className="text-white/50">Expires in</span>
              <span className="rounded-full bg-white/5 px-2.5 py-0.5 font-semibold tabular-nums text-[var(--red,#F50537)]">
                {mmss}
              </span>
            </div>
            <p className="mt-2 text-[11px] text-white/30">
              A new code is generated automatically when this one expires.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default ConnectTvPopUp;